import { useCallback, useEffect, useState } from "react";
import styled, { keyframes } from "styled-components";
import { SP_BREAKPOINT } from "../../constants/app";
import { PRIMARY_DARK_GREEN } from "../../constants/color";
import { SidebarMenu } from "../../ducks/app";
import { ArrowIcon } from "./icons/ArrowIcon";
import { IconComponent } from "./icons/types";

type Props = {
  menu: SidebarMenu;
  selectedMenuPath: string;
  onClick: (path: string) => void;
};

export const SidebarMenuItem: React.FC<Props> = ({
  menu,
  selectedMenuPath,
  onClick,
}) => {
  const [isSubMenuOpen, setIsSubMenuOpen] = useState<boolean | null>(null);

  useEffect(() => {
    if (!menu.subMenus || menu.subMenus.length === 0) return;

    const isSubMenuOpen = menu.subMenus.some(
      (submenu) => selectedMenuPath === submenu.path,
    );

    if (isSubMenuOpen) setIsSubMenuOpen(isSubMenuOpen);
  }, [selectedMenuPath, menu]);

  const handleOnClick = useCallback((hasSubMenu: boolean, path: string) => {
    if (hasSubMenu) setIsSubMenuOpen((prev) => !prev);
    else onClick(path);
  }, []);

  const _renderMenuItem = useCallback(
    (
      title: string,
      hasSubMenu: boolean,
      isSubMenu: boolean,
      path?: string,
      Icon?: IconComponent,
      key?: string,
    ) => {
      const isSelected = selectedMenuPath.includes(path ?? "") && !hasSubMenu;

      return (
        <StyledSidebarMenuItemContainer
          key={key}
          $isSelected={isSelected}
          $isSubMenu={isSubMenu}
          onClick={() => handleOnClick(hasSubMenu, path ?? "")}
        >
          <StyledSidebarMenuItemContent>
            {Icon && (
              <Icon fillColor={isSelected ? PRIMARY_DARK_GREEN : undefined} />
            )}

            <StyledSidebarMenuItemTitle $isSelected={isSelected}>
              {title}
            </StyledSidebarMenuItemTitle>
          </StyledSidebarMenuItemContent>

          {hasSubMenu && (
            <StyledArrowIcon $isSubMenuOpen={isSubMenuOpen}>
              <ArrowIcon
                fillColor={isSelected ? PRIMARY_DARK_GREEN : undefined}
              />
            </StyledArrowIcon>
          )}
        </StyledSidebarMenuItemContainer>
      );
    },
    [isSubMenuOpen, selectedMenuPath],
  );

  return (
    <div>
      {_renderMenuItem(
        menu.title,
        (menu.subMenus && menu.subMenus.length > 0) ?? false,
        false,
        menu.path,
        menu.icon,
      )}

      <StyledSubMenuContainer $isSubMenuOpen={isSubMenuOpen}>
        {menu.subMenus && <div style={{ height: "5px" }}></div>}
        {menu.subMenus &&
          menu.subMenus.map((submenu, i) =>
            _renderMenuItem(
              submenu.title,
              false,
              true,
              submenu.path,
              submenu.icon,
              `${menu.title}-submenu-${i}`,
            ),
          )}
      </StyledSubMenuContainer>
    </div>
  );
};

const StyledSidebarMenuItemContainer = styled.div<{
  $isSelected: boolean;
  $isSubMenu: boolean;
}>`
  display: flex;
  width: ${({ $isSubMenu }) => ($isSubMenu ? "224px" : "248px")};
  height: 48px;
  padding: 14px;
  align-items: center;
  gap: 5px;
  flex-shrink: 0;
  border-radius: 8px;
  background-color: ${({ $isSelected }) =>
    $isSelected ? "#f2f2f2" : "#ffffff"};
  cursor: pointer;

  &:hover {
    background-color: #f2f2f2;
  }

  @media (max-width: ${SP_BREAKPOINT}) {
    width: ${({ $isSubMenu }) => ($isSubMenu ? "224px" : "292px")};
  }
`;

const StyledSidebarMenuItemContent = styled.div`
  display: flex;
  width: 191px;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
`;

const StyledSidebarMenuItemTitle = styled.div<{ $isSelected: boolean }>`
  color: ${({ $isSelected }) => ($isSelected ? PRIMARY_DARK_GREEN : "#333333")};
  font-size: 14px;
  font-weight: 700;
  line-height: 100%;
`;

const rotate = ($isSubMenuOpen: boolean | null) => keyframes`
  from {
    transform: rotate(${($isSubMenuOpen === null ? 0 : $isSubMenuOpen ? 0 : 180) + "deg"});
  }
  to {
    transform: rotate(${($isSubMenuOpen === null ? 0 : $isSubMenuOpen ? 180 : 0) + "deg"});
  }
`;

const StyledArrowIcon = styled.div<{ $isSubMenuOpen: boolean | null }>`
  transform: ${({ $isSubMenuOpen }) =>
    $isSubMenuOpen === null
      ? "rotate(0deg)"
      : $isSubMenuOpen
        ? "rotate(180deg)"
        : "rotate(0deg)"};
  animation: ${({ $isSubMenuOpen }) => rotate($isSubMenuOpen)} 0.2s forwards;
`;

const StyledSubMenuContainer = styled.div<{ $isSubMenuOpen: boolean | null }>`
  max-height: ${({ $isSubMenuOpen }) => ($isSubMenuOpen ? "300px" : "0")};
  overflow: hidden;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 5px;
  transition: max-height 0.2s ease-in-out;

  @media (max-width: ${SP_BREAKPOINT}) {
    align-items: flex-start;
    padding-left: 24px;
  }
`;
